import 'core-js/actual/object/has-own'
import React, { ReactNode, useMemo, useState, useCallback } from 'react'
import Markdown, { Options, ExtraProps } from 'react-markdown'
import remarkGfm from 'remark-gfm'
// import { visit } from 'unist-util-visit'

import {
  YStack,
  styled,
  View,
  Token,
  getConfig,
  getFontSize,
  FontSizeTokens,
  StackProps,
} from 'tamagui'
import { ExternalLink, H2, Strong, Text, Paragraph } from '../index'
import { GUILD_ALLOWED_ELEMENTS } from '@data/markdown/guildAllowedElements'

import './FormattedMarkdown.css'

const FormattedMarkdownVariantEnum = {
  FULL_RICH_TEXT: 'FULL_RICH_TEXT',
  PLAIN_TEXT: 'PLAIN_TEXT',
} as const

type FormattedMarkdownProps = {
  formattedVariant?: keyof typeof FormattedMarkdownVariantEnum
  textSize?: FontSizeTokens
  numberOfLines?: number
  expandMarkdownChildren?: React.ReactNode
} & Options

const REMARK_PLUGINS = [
  // Formats plain urls (www.guild.host, https://www.guild.host)
  remarkGfm,
]

// const REHYPE_PLUGINS = [
//   function rehypePluginAddingIndex() {
//     return (tree) => {
//       visit(tree, (node, index) => {
//         if (node.type === 'element' && typeof index === 'number') {
//           if (!node.properties) {
//             node.properties = {}
//           }

//           node.properties.index = index
//         }
//       })
//     }
//   },
// ]

// Driven by shared config — single source of truth
const allowedElements: string[] = [...GUILD_ALLOWED_ELEMENTS]

function renderHeading(props: {
  node: { tagName: string }
  children?: ReactNode
}) {
  // All headings render as H2 visually (h1 markdown → H2 display)
  return <H2 children={props.children} />
}

function renderOrderedList(props: { children?: ReactNode }) {
  return <OrderedList gap='$1'>{props.children}</OrderedList>
}

function renderUnorderedList(props: { children?: ReactNode }) {
  return <UnorderedList gap='$1'>{props.children}</UnorderedList>
}

/**
 * Map html elements to Guild react-native components.
 */
function useMarkDownProps({
  formattedVariant,
  textSize = '$5',
  ...markdownProps
}: FormattedMarkdownProps): Options {
  // @ts-ignore Custom component signatures don't match react-markdown's expected types
  return useMemo(() => {
    function a({ children, href }: { children?: ReactNode; href?: string }) {
      // CSS 'inherit' is web-only, cast through unknown
      const inheritStyle = 'inherit' as unknown as undefined
      const inheritFontWeight = 'inherit' as unknown as undefined
      if (!href) {
        return (
          <Text fontSize={inheritStyle} lineHeight={inheritStyle} fontWeight={inheritFontWeight}>
            {children}
          </Text>
        )
      }

      return (
        <ExternalLink
          href={href}
          color='$darkBlue9'
          fontSize={inheritStyle}
          lineHeight={inheritStyle}
          fontWeight={inheritFontWeight}
        >
          {children}
        </ExternalLink>
      )
    }

    function li({ children }: { children?: React.ReactNode }) {
      const listItemChildren = useMemo(() => {
        if (Array.isArray(children)) {
          const arrayChildren = children.reduce((memo, child) => {
            if (typeof child === 'string') {
              if (!child.trim()) {
                return memo
              }

              return [...memo, <Text key={memo.length} size={textSize}>{child}</Text>]
            }

            return [...memo, child]
          }, [] as Array<React.ReactNode>)

          if (arrayChildren.length === 1) {
            return arrayChildren[0]
          }

          return arrayChildren
        }

        if (typeof children === 'string') {
          return <Text size={textSize}>{children}</Text>
        }

        return children
      }, [children, textSize])

      const liStyle = useMemo(() => {
        return {
          fontSize: getFontSize(textSize),
          lineHeight: getFontSize(textSize) * ((1 + Math.pow(5, 0.25)) / 2),
        }
      }, [textSize])

      return <Li style={liStyle}>{listItemChildren}</Li>
    }

    function p({ children }: { children?: React.ReactNode }) {
      if (Array.isArray(children)) {
        if (
          children.length === 1 &&
          typeof children[0] === 'string' &&
          children[0].endsWith('\n')
        ) {
          return <Paragraph size={textSize}>{children[0]}</Paragraph>
        }

        return (
          <Paragraph size={textSize}>
            {children.map((child, index) => {
              if (typeof child === 'string') {
                if (child.includes('\n')) {
                  return (<React.Fragment key={index}>{child
                    .split('\n')
                    .map((stringSplit, splitIndex) => {
                      if (!stringSplit) {
                        return null
                      }

                      return (
                        <Text size={textSize} key={splitIndex}>
                          {stringSplit}
                        </Text>
                      )
                    })
                    .reduce((memo, item, reduceIndex, initialArray) => {
                      if (reduceIndex === initialArray.length - 1) {
                        if (item === null) {
                          return memo
                        }
                        return [...memo, item]
                      }

                      if (item === null) {
                        return [
                          ...memo,
                          <br key={initialArray.length + reduceIndex} />,
                        ]
                      }

                      return [
                        ...memo,
                        item,
                        <br key={initialArray.length + reduceIndex} />,
                      ]
                    }, [] as Array<React.JSX.Element>)}</React.Fragment>)
                }

                // CSS 'inherit' is web-only
                const inheritStyle = 'inherit' as unknown as undefined
                return (
                  <Text key={index} fontSize={inheritStyle} lineHeight={inheritStyle}>
                    {child}
                  </Text>
                )
              }

              return <React.Fragment key={index}>{child}</React.Fragment>
            })}
          </Paragraph>
        )
      }

      if (typeof children === 'string') {
        const textParts = children.split('\n').map((child) => `${child}\n`)

        return p({ children: textParts })
      }

      return <Paragraph size={textSize}>{children}</Paragraph>
    }

    function em({ children }: { children?: React.ReactNode }) {
      return (
        <Text fontStyle='italic' size={textSize}>
          {children}
        </Text>
      )
    }

    function strong({ children }: { children?: React.ReactNode }) {
      // CSS 'inherit' is web-only
      const inheritStyle = 'inherit' as unknown as undefined
      return (
        <Strong fontSize={inheritStyle} lineHeight={inheritStyle}>
          {children}
        </Strong>
      )
    }

    switch (formattedVariant) {
      case FormattedMarkdownVariantEnum.PLAIN_TEXT:
        return {
          ...markdownProps,
          children: (markdownProps.children ?? '').trim(),
          components: {
            a,
            h1: p,
            li,
            p,
            em,
            ol: renderOrderedList,
            ul: renderUnorderedList,
          },
          allowedElements: ['a', 'h1', 'li', 'ol', 'p', 'ul'],
        }
      case FormattedMarkdownVariantEnum.FULL_RICH_TEXT:
      default:
        return {
          ...markdownProps,
          allowedElements,
          children: (markdownProps.children ?? '').trim(),
          // our custom `components` should always be enforced,
          // because we have to translate the web html elements to react-native.
          // should match allowedElements
          components: {
            a,
            h1: renderHeading,
            li,
            ol: renderOrderedList,
            ul: renderUnorderedList,
            p,
            em,
            strong,
          },
        }
    }
  }, [formattedVariant, textSize, markdownProps])
}

const LimitNumberOfLines = ({
  numberOfLines,
  textSize,
  children,
  expandMarkdownChildren,
}: {
  numberOfLines: number
  textSize: FontSizeTokens
  children: React.ReactNode
  expandMarkdownChildren?: React.ReactNode
}) => {
  const lineHeight = useMemo(() => {
    return getFontSize(textSize) * ((1 + Math.pow(5, 0.25)) / 2)
  }, [textSize])

  const [dimensions, setDimensions] = useState<{ height: number }>({
    height: 0,
  })

  const onLayout = useCallback(
    ({ nativeEvent: { layout } }: { nativeEvent: { layout: { height: number } } }) => {
      setDimensions(layout)
    },
    [setDimensions]
  )

  if (!lineHeight) {
    console.warn(
      'Could not find lineHeight to limit Markdown Number Of Lines',
      textSize
    )

    return <>{children}</>
  }

  const maxAllowedHeight = lineHeight * numberOfLines
  const limitNumberOfLines = maxAllowedHeight < dimensions.height

  return (
    <>
      <StyledLimitNumberOfLines
        style={{
          maxHeight: maxAllowedHeight,
          pointerEvents:
            limitNumberOfLines && expandMarkdownChildren ? 'none' : 'auto',
        }}
        className={
          limitNumberOfLines ? 'LimitNumberOfLinesGradient' : undefined
        }
      >
        <View onLayout={onLayout}>{children}</View>
      </StyledLimitNumberOfLines>

      {limitNumberOfLines ? expandMarkdownChildren : null}
    </>
  )
}

export const FormattedMarkdown = YStack.styleable<
  FormattedMarkdownProps & { stackProps?: StackProps }
>(({ numberOfLines, expandMarkdownChildren, stackProps = {}, ...props }, ref) => {
  const markdownProps = useMarkDownProps(props)

  if (numberOfLines) {
    return (
      <YStack ref={ref} {...stackProps}>
        <LimitNumberOfLines
          numberOfLines={numberOfLines}
          textSize={props.textSize || '$5'}
          expandMarkdownChildren={expandMarkdownChildren}
        >
          <YStack gap='$3'>
            <Markdown
              remarkPlugins={REMARK_PLUGINS}
              // rehypePlugins={REHYPE_PLUGINS}
              unwrapDisallowed
              {...markdownProps}
            />
          </YStack>
        </LimitNumberOfLines>
      </YStack>
    )
  }

  return (
    <YStack ref={ref} gap='$3' {...stackProps}>
      <Markdown
        remarkPlugins={REMARK_PLUGINS}
        // rehypePlugins={REHYPE_PLUGINS}
        unwrapDisallowed
        {...markdownProps}
      />
    </YStack>
  )
})

const OrderedList = styled(YStack, {
  tag: 'ol',
  paddingLeft: '$6',
  marginBottom: 0,

  style: {
    listStyleType: 'decimal',
  },
} as const)

const UnorderedList = styled(YStack, {
  tag: 'ul',
  paddingLeft: 0,
  marginBottom: 0,
  marginLeft: 20,

  style: {
    listStyleType: 'disc',
  },
} as const)

const Li = styled(View, {
  tag: 'li',
  className: 'ListItem',

  paddingLeft: 0, // '$2',
  // @ts-expect-error list-item is valid CSS display value
  display: 'list-item',
} as const)

const StyledLimitNumberOfLines = styled(YStack, {
  // @ts-expect-error overflowY is valid CSS
  overflowY: 'hidden',
} as const)
